import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Manrope-Medium.ttf", fontWeight: "medium" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    paddingBottom: 60,
    fontFamily: "Manrope",
  },
  headerSpace: {
    height: 80,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    marginBottom: 10,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  flexRow: {
    flexDirection: "row",
    justifyContent: "left",
    alignItems: "center",
    marginTop: 3,
    marginBottom: 3,
  },
  mainTitle2: {
    fontSize: 14,
    textAlign: "center",
    fontWeight: "bold",
    marginBottom: 4,
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },
  smallText: {
    fontSize: 9,
    color: "grey",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 2,
  },
  accountDetails: {
    textAlign: "right",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 4,
    marginBottom: 2,
    backgroundColor: "#f5f5f5",
  },
  belowtableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 4,
    marginTop: 14,
    marginBottom: 2,
    backgroundColor: "#f5f5f5",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 4,
    alignItems: "flex-start",
  },
  cell: {
    fontSize: 9,
  },
  cellBold: {
    fontSize: 9,
    fontWeight: "bold",
  },
  cellGreen: {
    fontSize: 9,
    color: "green",
    fontWeight: "bold",
  },
  cellRed: {
    fontSize: 9,
    color: "red",
    fontWeight: "bold",
  },
  totalsRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#000",
    marginTop: 6,
    paddingTop: 6,
    alignItems: "flex-start",
  },

  footerSpace: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 60,
  },
  footer_image: {
    width: "100%",
    height: 60,
    objectFit: "cover",
  },
  footerRow: {
    position: "absolute",
    bottom: 64,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "center",
    borderTopWidth: 1,
    borderTopColor: "#ccc",
    paddingTop: 6,
    textAlign: "center",
  },
  footer: {
    fontSize: 9,
    color: "grey",
  },
  pageNumber: {
    fontSize: 9,
    color: "grey",
  },
  centerDiv: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
});

const COL = {
  invDate: 1.0,
  inv: 1.4,
  lpo: 1.4,
  debit: 0.8,
  credit: 0.8,
  balance: 0.9,
};

const OutstandingDocument = ({
  outstandingData,
  accountData,
  totals,
  outstandingDataRecordsFromAPI,
  outstandingDataAccountName,
  outstandingDataAccount,
  outstandingDataAccountTRN,
  agingData,
}) => {
  console.log("outstandingDataRecordsFromAPI", outstandingDataRecordsFromAPI)
  const apiPaths = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

  const asOnDate = dayjs(accountData?.toDate) || dayjs();

  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* LETTERHEAD */}
        <View style={styles.headerSpace} fixed>
          {apiPaths[0]?.REP_HEADER?.includes("data:image/") && (
            <Image
              src={apiPaths[0].REP_HEADER}
              style={{ width: "100%", height: 78 }}
            />
          )}
        </View>
        <View style={styles.centerDiv}>
          <Text style={styles.mainTitle}>Statement of Accounts</Text>
          <Text style={styles.smallText}>
            As of{" "}
            {dayjs(accountData?.toDate).format("DD/MM/YYYY") ||
              dayjs().format("DD/MM/YYYY")}
          </Text>
        </View>

        {/* HEADER ROW */}
        <View style={styles.headerRow}>
          <View style={{ flex: 1 }}>
            <View style={styles.flexRow}>
              <Text style={styles.smallText}>Account:</Text>

              <Text style={styles.sectionTitle}>{outstandingDataAccount}</Text>
            </View>
            <Text style={styles.sectionTitle}>
              {outstandingDataAccountName}
            </Text>
            <Text style={styles.smallText}>
              TRN: {outstandingDataAccountTRN}
            </Text>
            <Text style={styles.smallText}>
              Phone: {outstandingDataRecordsFromAPI[0].phone}
            </Text>
          </View>
          <View style={styles.accountDetails}>
            <Text style={styles.smallText}>Period</Text>
            <Text style={styles.sectionTitle}>
              {dayjs(accountData?.fromDate).format("DD/MM/YYYY")} —{" "}
              {dayjs(accountData?.toDate).format("DD/MM/YYYY")}
            </Text>
            <Text style={styles.smallText}>
              Total Records: {outstandingDataRecordsFromAPI.length}
            </Text>
            <Text style={styles.smallText}>
              Total Outstanding Balance: {Math.abs(totals.balance).toFixed(2)}
            </Text>
          </View>
        </View>

        {/* TABLE HEADER */}
        <View style={styles.tableHeader}>
          <View style={{ flex: COL.invDate }}>
            <Text style={styles.cellBold}>Inv Date</Text>
          </View>
          <View style={{ flex: COL.inv }}>
            <Text style={styles.cellBold}>Invoice</Text>
          </View>
          {/* <View style={{ flex: COL.account }}>
            <Text style={styles.cellBold}>Account</Text>
          </View>
          <View style={{ flex: COL.name }}>
            <Text style={styles.cellBold}>Name</Text> 
          </View>*/}
          <View style={{ flex: COL.lpo }}>
            <Text style={styles.cellBold}>LPO</Text>
          </View>
          <View style={{ flex: COL.debit }}>
            <Text style={[styles.cellBold, { textAlign: "right" }]}>Debit</Text>
          </View>
          <View style={{ flex: COL.credit }}>
            <Text style={[styles.cellBold, { textAlign: "right" }]}>
              Credit
            </Text>
          </View>
          <View style={{ flex: COL.balance }}>
            <Text style={[styles.cellBold, { textAlign: "right" }]}>
              Balance
            </Text>
          </View>
        </View>

        {/* TABLE ROWS */}
        {outstandingDataRecordsFromAPI?.map((r, idx) => (
          <View key={idx} style={styles.tableRow}>
            <View style={{ flex: COL.invDate }}>
              <Text style={styles.cell}>
                {r.INVDATE ? dayjs(r.INVDATE).format("DD-MM-YYYY") : "-"}
              </Text>
            </View>

            <View style={{ flex: COL.inv }}>
              <Text style={styles.cell}>{r.INV || "-"}</Text>
            </View>

            {/* <View style={{ flex: COL.account }}>
              <Text style={styles.cell}>{r.ACCOUNT || "-"}</Text>
            </View>

            <View style={{ flex: COL.name }}>
              <Text style={styles.cell}>{r.NAME || "-"}</Text>
            </View> */}

            {/* ✅ LPO wrapped in View so text wraps to next line */}
            <View style={{ flex: COL.lpo }}>
              <Text style={styles.cell}>{r.DESCRIPTION || "-"}</Text>
            </View>

            <View style={{ flex: COL.debit }}>
              <Text style={[styles.cellGreen, { textAlign: "right" }]}>
                {r.DEBIT ? Number(r.DEBIT).toFixed(2) : "-"}
              </Text>
            </View>

            <View style={{ flex: COL.credit }}>
              <Text style={[styles.cellRed, { textAlign: "right" }]}>
                {r.CREDIT ? Number(r.CREDIT).toFixed(2) : "-"}
              </Text>
            </View>

            <View style={{ flex: COL.balance }}>
              <Text
                style={[
                  styles.cellBold,
                  {
                    textAlign: "right",
                    color: Number(r.BALANCE) < 0 ? "red" : "#111",
                  },
                ]}
              >
                {r.BALANCE ? Number(r.BALANCE).toFixed(2) : "-"}
              </Text>
            </View>
          </View>
        ))}

        {/* TOTALS ROW */}
        <View style={styles.totalsRow}>
          <View style={{ flex: COL.invDate }}>
            <Text style={styles.cellBold}>TOTAL</Text>
          </View>
          <View style={{ flex: COL.inv }} />
          <View style={{ flex: COL.account }} />
          <View style={{ flex: COL.name }} />
          <View style={{ flex: COL.lpo }} />
          <View style={{ flex: COL.debit }}>
            <Text style={[styles.cellGreen, { textAlign: "right" }]}>
              {totals.debit.toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: COL.credit }}>
            <Text style={[styles.cellRed, { textAlign: "right" }]}>
              {totals.credit.toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: COL.balance }}>
            <Text
              style={[
                styles.cellBold,
                {
                  textAlign: "right",
                  color: Number(Number(totals?.debit.toFixed(2) - totals?.credit.toFixed(2)).toFixed(2)) < 0 ? "red" : "#111",
                },
              ]}
            >
              {/* {Math.abs(totals.balance).toFixed(2)} */}
              {Number(totals?.debit.toFixed(2) - totals?.credit.toFixed(2)).toFixed(2)}
            </Text>
          </View>
        </View>

        {/* AGING HEADER */}
        <View style={styles.belowtableHeader}>
          <Text style={[styles.cellBold, { flex: COL.invDate }]}>30 Days</Text>
          <Text style={[styles.cellBold, { flex: COL.invDate }]}>60 Days</Text>
          <Text style={[styles.cellBold, { flex: COL.invDate }]}>90 Days</Text>
          <Text style={[styles.cellBold, { flex: COL.invDate }]}>120 Days</Text>
          <Text style={[styles.cellBold, { flex: COL.invDate }]}>&gt; 120 Days</Text>
          {/* <Text style={[styles.cellBold, { flex: COL.invDate }]}>
            + 150 Days
          </Text> */}
          <Text style={[styles.cellBold, { flex: COL.invDate }]}>Total</Text>
        </View>

        {/* AGING DATA ROW */}
        <View style={[styles.tableRow, { marginTop: 2 }]}>
          <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.["0_30"] || 0).toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.["31_60"] || 0).toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.["61_90"] || 0).toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.["91_120"] || 0).toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.["120_PLUS"] || 0).toFixed(2)}
            </Text>
          </View>
          {/* <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.["120_PLUS"] || 0).toFixed(2)}
            </Text>
          </View> */}
          <View style={{ flex: COL.invDate }}>
            <Text style={[styles.cellBold]}>
              {Number(agingData?.TOTAL_OUTSTANDING || 0).toFixed(2)}
            </Text>
          </View>
        </View>

        <View style={styles.footerSpace} fixed>
          {apiPaths_from_local_storage[0]?.REP_FOOTER?.includes(
            "data:image/",
          ) ? (
            <Image
              src={apiPaths_from_local_storage[0].REP_FOOTER}
              style={styles.footer_image}
            />
          ) : null}
        </View>

        {/* FOOTER */}
        <View style={styles.footerRow} fixed>
          <Text style={styles.footer}>
            Generated: {dayjs().format("DD/MM/YYYY HH:mm")}
          </Text>
          <Text
            style={styles.footer}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
            fixed
          />
        </View>
      </Page>
    </Document>
  );
};

export default OutstandingDocument;
